import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BlockerFunction, useBlocker } from "react-router-dom";
import { Container, Content, Header } from "rsuite";

import useBeforeUnload from "hooks/useBeforeUnload";

import AppRouterContainer from "components/App/AppRouter/AppRouterContainer";
import AppHeaderContainer from "components/AppHeader/AppHeaderContainer";
import BreadCrumb from "components/BreadCrumb/BreadCrumb";
import ConfirmModal from "components/Common/ConfirmModal/ConfirmModal";
import ErrorSectionContainer from "components/Common/ErrorSection/ErrorSectionContainer";
import PreloaderContainer from "components/Common/Preloader/PreloaderContainer";
import AppRegistrationInformationContainer from "components/Environments/AppRegistrationInformation/AppRegistrationInformationContainer";
import EnvironmentHistoryContainer from "components/Environments/EnvironmentHistory/EnvironmentHistoryContainer";
import TenantStatusContainer from "components/Environments/Tenants/TenantStatus/TenantStatusContainer";
import UnsavedDataModalContainer from "components/UnsavedDataModal/UnsavedDataModalContainer";

import css from "./MainLayout.module.css";

interface ComponentProps {
    isAppSettingsFetched: boolean;
    isLoaderVisible: boolean;
    isErrorMessageVisible: boolean;
    isUnsavedChangeAvailable: boolean;
    fetchAppSettings: () => void;
    fetchUserPermissions: () => void;
    fetchSupportContact: () => void;
    setIsUnsavedChangeAvailable: (isAvailable: boolean) => void;
}

const MainLayout: React.FC<ComponentProps> = ({
    isAppSettingsFetched,
    isLoaderVisible,
    isErrorMessageVisible,
    isUnsavedChangeAvailable,
    fetchAppSettings,
    fetchUserPermissions,
    fetchSupportContact,
    setIsUnsavedChangeAvailable,
}: ComponentProps) => {
    const { t } = useTranslation();
    useBeforeUnload();

    const shouldBlock = React.useCallback<BlockerFunction>(
        ({ currentLocation, nextLocation }) =>
            isUnsavedChangeAvailable &&
            currentLocation.pathname !== nextLocation.pathname,
        [isUnsavedChangeAvailable]
    );
    const blocker = useBlocker(shouldBlock);

    const [isUserPermissionsLoaded, setUserPermissionsLoaded] = useState(false);
    const [isSupportContactLoaded, setIsSupportContactLoaded] = useState(false);

    useEffect(() => {
        if (!isAppSettingsFetched) {
            fetchAppSettings();
        }
    }, [isAppSettingsFetched, fetchAppSettings]);

    useEffect(() => {
        if (!isUserPermissionsLoaded) {
            fetchUserPermissions();
            setUserPermissionsLoaded(true);
        }
    }, [isUserPermissionsLoaded, fetchUserPermissions]);

    useEffect(() => {
        if (!isSupportContactLoaded) {
            fetchSupportContact();
            setIsSupportContactLoaded(true);
        }
    }, [isSupportContactLoaded, fetchSupportContact]);

    const proceedNavigation = () => {
        if (blocker.proceed) {
            setIsUnsavedChangeAvailable(false);
            blocker.proceed();
        }
    };

    const cancelNavigation = () => {
        if (blocker.reset) {
            blocker.reset();
        }
    };

    return (
        <Container>
            <ConfirmModal
                cancel={t("unsavedDataModal.button.stay")}
                confirm={t("unsavedDataModal.button.leave")}
                open={blocker.state === "blocked"}
                message={t("unsavedDataModal.mainMessage")}
                onCancel={cancelNavigation}
                onConfirm={proceedNavigation}
            />
            <UnsavedDataModalContainer />

            <Header>
                <AppHeaderContainer />
            </Header>

            <Content className={css.content}>
                {isLoaderVisible && <PreloaderContainer />}
                {isErrorMessageVisible && <ErrorSectionContainer />}

                <div
                    className={
                        isLoaderVisible ||
                        isErrorMessageVisible ||
                        !isAppSettingsFetched
                            ? css.hiddenContent
                            : css.mainContent
                    }
                >
                    <BreadCrumb />
                    <AppRouterContainer />
                    <EnvironmentHistoryContainer />
                    <AppRegistrationInformationContainer />
                    <TenantStatusContainer />
                </div>
            </Content>
        </Container>
    );
};

export default MainLayout;
